import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useToast } from '@shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@shared/utils/utils';
import {
  AmazonFbmShipmentTrackingApiAdapter,
  type AmazonFbmShipmentTrackingPreviewResponse,
} from '../../adapters/AmazonFbmShipmentTrackingApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

type Step = 'filters' | 'preview' | 'result';

export interface AmazonFbmShipmentExportModalRef {
  open: () => void;
  close: () => void;
}

interface AmazonFbmShipmentExportModalProps {
  shopId: number;
  onSuccess?: () => void;
}

const adapter = new AmazonFbmShipmentTrackingApiAdapter(createHttpClient(apiClientWH));

function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Convierte Y-m-d a fecha legible según el idioma de la app. */
function formatYmdForLocale(ymd: string | undefined, locale: string): string {
  if (!ymd) return '';
  const parts = ymd.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return ymd;
  const [y, m, day] = parts;
  return new Date(y, m - 1, day).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const PREVIEW_COLUMN_KEYS = [
  'order-id',
  'order-item-id',
  'quantity',
  'ship-date',
  'carrier-code',
  'carrier-name',
  'tracking-number',
  'ship-method',
] as const;

const PREVIEW_COLUMN_I18N_KEY: Record<(typeof PREVIEW_COLUMN_KEYS)[number], string> = {
  'order-id': 'orderId',
  'order-item-id': 'orderItemId',
  quantity: 'quantity',
  'ship-date': 'shipDate',
  'carrier-code': 'carrierCode',
  'carrier-name': 'carrierName',
  'tracking-number': 'trackingNumber',
  'ship-method': 'shipMethod',
};

function amazonSellerFeedsUrl(locale: string): string {
  return locale.startsWith('es')
    ? 'https://sellercentral.amazon.es/order-reports-and-feeds/feeds'
    : 'https://sellercentral.amazon.com/order-reports-and-feeds/feeds';
}

function formatPreviewCellValue(columnKey: string, raw: string | undefined, locale: string): string {
  if (raw == null || raw === '') return '—';
  if (columnKey === 'ship-date' && /^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    try {
      return new Date(raw).toLocaleString(locale, {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return raw;
    }
  }
  return raw;
}

export const AmazonFbmShipmentExportModal = forwardRef<
  AmazonFbmShipmentExportModalRef,
  AmazonFbmShipmentExportModalProps
>(({ shopId, onSuccess }, ref) => {
  const { t, i18n } = useTranslation('shopsModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const modalRef = useRef<ModalCenterRef>(null);

  const [step, setStep] = useState<Step>('filters');
  const [dateFrom, setDateFrom] = useState(todayYmd);
  const [dateTo, setDateTo] = useState(todayYmd);
  const [previewData, setPreviewData] = useState<AmazonFbmShipmentTrackingPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const resetState = () => {
    setStep('filters');
    const d = todayYmd();
    setDateFrom(d);
    setDateTo(d);
    setPreviewData(null);
    setError(null);
  };

  useImperativeHandle(ref, () => ({
    open: () => {
      resetState();
      modalRef.current?.openModal();
    },
    close: () => modalRef.current?.closeModal(),
  }));

  const queryParams = useMemo(
    () => ({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      shop_id: shopId,
    }),
    [dateFrom, dateTo, shopId],
  );

  const isDateRangeValid = useMemo(() => {
    if (!dateFrom || !dateTo) return false;
    return dateFrom <= dateTo;
  }, [dateFrom, dateTo]);

  const feedsUrl = useMemo(() => amazonSellerFeedsUrl(i18n.language), [i18n.language]);

  const feedsLinkClassName =
    'font-medium text-primary underline underline-offset-2 transition-colors hover:text-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm';

  const previewHeaders = useMemo(() => {
    const rows = previewData?.preview_rows;
    if (!rows?.length) return [];
    const sample = rows[0];
    return PREVIEW_COLUMN_KEYS.filter((k) => k in sample);
  }, [previewData]);

  const handlePreview = async () => {
    if (!isDateRangeValid) {
      toast({
        title: t('modals.amazonFbmShipmentExport.toast.invalidDates'),
        variant: 'destructive',
      });
      return;
    }
    setError(null);
    setIsLoading(true);
    setPreviewData(null);
    try {
      const data = await adapter.preview(queryParams);
      setPreviewData(data);
      setStep('preview');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || t('toasts.error.unexpected');
      setError(message);
      toast({
        title: t('modals.settings.toast.error.title'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!isDateRangeValid) {
      toast({
        title: t('modals.amazonFbmShipmentExport.toast.invalidDates'),
        variant: 'destructive',
      });
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      await adapter.downloadShipmentTracking(queryParams);
      toast({
        title: t('modals.settings.toast.success.title'),
        description: t('modals.amazonFbmShipmentExport.toast.downloadSuccess'),
        variant: 'default',
      });
      await queryClient.invalidateQueries({ queryKey: ['orders'], exact: false });
      onSuccess?.();
      setStep('result');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || t('toasts.error.unexpected');
      setError(message);
      toast({
        title: t('modals.settings.toast.error.title'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      modalRef.current?.closeModal();
    }
  };

  const renderStepContent = () => {
    if (step === 'filters') {
      return (
        <>
          <p className="text-sm text-complementary-text">{t('modals.amazonFbmShipmentExport.description')}</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fbm-export-date-from">{t('modals.amazonFbmShipmentExport.dateFrom')}</Label>
              <Input
                id="fbm-export-date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fbm-export-date-to">{t('modals.amazonFbmShipmentExport.dateTo')}</Label>
              <Input
                id="fbm-export-date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>
          <p className="text-xs text-complementary-text">{t('modals.amazonFbmShipmentExport.dateHint')}</p>
          {!isDateRangeValid && dateFrom && dateTo && (
            <p className="text-sm text-error">{t('modals.amazonFbmShipmentExport.dateRangeError')}</p>
          )}
          {error && <p className="text-sm text-error">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="lg" onClick={handleClose} disabled={isLoading}>
              {t('modals.settings.buttons.cancel')}
            </Button>
            <Button variant="primary" size="lg" onClick={handlePreview} disabled={!isDateRangeValid || isLoading}>
              {isLoading ? t('common.loading') : t('modals.amazonFbmShipmentExport.preview')}
            </Button>
          </div>
        </>
      );
    }

    if (step === 'preview' && previewData) {
      const dateFromLabel = formatYmdForLocale(previewData.filters?.date_from, i18n.language);
      const dateToLabel = formatYmdForLocale(previewData.filters?.date_to, i18n.language);

      return (
        <>
          <div className="mb-4 space-y-3 rounded-lg border border-card-border bg-card-bg p-4">
            <h4 className="text-sm font-semibold text-foreground">
              {t('modals.amazonFbmShipmentExport.previewResults.summary')}
            </h4>
            <p className="text-sm leading-relaxed text-foreground">
              {t('modals.amazonFbmShipmentExport.summaryPeriod', {
                from: dateFromLabel,
                to: dateToLabel,
              })}
            </p>
            <ul className="list-inside list-disc space-y-1.5 text-sm text-complementary-text">
              <li>
                {t('modals.amazonFbmShipmentExport.summaryBulletOrders', {
                  count: previewData.orders_in_query,
                })}
              </li>
              <li>
                {t('modals.amazonFbmShipmentExport.summaryBulletLines', {
                  count: previewData.total_rows,
                })}
              </li>
              {previewData.skipped.length > 0 && (
                <li>
                  {t('modals.amazonFbmShipmentExport.summaryBulletSkipped', {
                    count: previewData.skipped.length,
                  })}
                </li>
              )}
            </ul>
          </div>

          {previewData.skipped.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-2 text-sm font-medium text-foreground">
                {t('modals.amazonFbmShipmentExport.skippedTitle')}
              </h5>
              <p className="mb-2 text-xs text-complementary-text">{t('modals.amazonFbmShipmentExport.skippedHint')}</p>
              <div className="max-h-[160px] overflow-auto rounded-md border border-border">
                <table className="w-full min-w-max text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-left">
                      <th className="p-2 font-medium">{t('modals.amazonFbmShipmentExport.colRef')}</th>
                      <th className="p-2 font-medium">{t('modals.amazonFbmShipmentExport.colWhatHappened')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.skipped.map((s, i) => (
                      <tr key={`${s.order_id}-${s.line_id}-${i}`} className="border-b border-border/60">
                        <td className="p-2 align-top font-medium text-foreground">{s.ref || `#${s.order_id}`}</td>
                        <td className="p-2 align-top text-complementary-text">
                          {t(`modals.amazonFbmShipmentExport.skippedReasons.${s.reason}`, {
                            defaultValue: s.message,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {previewData.preview_rows.length > 0 && (
            <div className="mb-4">
              <h4 className="mb-1 text-sm font-semibold">
                {t('modals.amazonFbmShipmentExport.previewResults.rowsTitle')}
              </h4>
              <p className="mb-2 text-xs text-complementary-text">
                {t('modals.amazonFbmShipmentExport.previewResults.sampleNote', {
                  limit: previewData.preview_limit,
                })}
              </p>
              <div
                className={cn(
                  'max-h-[min(480px,calc(100vh-16rem))] overflow-y-auto overflow-x-scroll rounded-lg border border-card-border',
                  'scrollbar-gutter-stable',
                )}
              >
                <table className="w-full min-w-max caption-bottom text-sm">
                  <TableHeader>
                    <TableRow>
                      {previewHeaders.map((h) => (
                        <TableHead key={h} className="whitespace-nowrap text-xs">
                          {t(`modals.amazonFbmShipmentExport.previewColumns.${PREVIEW_COLUMN_I18N_KEY[h]}`)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.preview_rows.map((row, ri) => (
                      <TableRow key={ri}>
                        {previewHeaders.map((h) => {
                          const raw = row[h];
                          const formatted = formatPreviewCellValue(h, raw, i18n.language);
                          const display =
                            h === 'carrier-code' && raw === 'Other'
                              ? t('modals.amazonFbmShipmentExport.carrierOther')
                              : formatted;
                          return (
                            <TableCell key={h} className="max-w-[200px] truncate text-xs">
                              {display}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </table>
              </div>
            </div>
          )}

          <Alert className="border-border bg-muted/40 border" role="alert">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <div className="min-w-0 space-y-1">
                <AlertTitle className="text-sm font-semibold text-foreground">
                  {t('modals.amazonFbmShipmentExport.exportDisclaimer.title')}
                </AlertTitle>
                <AlertDescription className="text-sm leading-snug text-complementary-text">
                  <Trans
                    i18nKey="modals.amazonFbmShipmentExport.exportDisclaimer.description"
                    t={t}
                    components={{
                      feedLink: (
                        <a
                          href={feedsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={feedsLinkClassName}
                          aria-label={t('modals.amazonFbmShipmentExport.amazonFeedsLinkAria')}
                        />
                      ),
                    }}
                  />
                </AlertDescription>
              </div>
            </div>
          </Alert>

          {error && <p className="mb-4 text-sm text-error">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="lg" onClick={handleClose} disabled={isLoading}>
              {t('modals.settings.buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleDownload}
              disabled={isLoading || previewData.total_rows === 0}
            >
              {isLoading ? t('common.loading') : t('modals.amazonFbmShipmentExport.download')}
            </Button>
          </div>
        </>
      );
    }

    if (step === 'result' && previewData) {
      return (
        <>
          <div className="space-y-3 rounded-lg border border-card-border bg-card-bg p-4">
            <h4 className="text-sm font-semibold text-foreground">
              {t('modals.amazonFbmShipmentExport.exportResults.title')}
            </h4>
            <p className="text-sm leading-relaxed text-complementary-text">
              <Trans
                i18nKey="modals.amazonFbmShipmentExport.exportResults.description"
                t={t}
                components={{
                  feedLink: (
                    <a
                      href={feedsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={feedsLinkClassName}
                      aria-label={t('modals.amazonFbmShipmentExport.amazonFeedsLinkAria')}
                    />
                  ),
                }}
              />
            </p>
            <p className="text-sm text-foreground">
              {t('modals.amazonFbmShipmentExport.exportResults.linesCount', {
                count: previewData.total_rows,
              })}
            </p>
          </div>
          <div className="flex justify-end">
            <Button variant="primary" size="lg" onClick={handleClose}>
              {t('modals.amazonFbmShipmentExport.close')}
            </Button>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modals.amazonFbmShipmentExport.title')}
      width="xxl"
      type="default"
      className="bg-card-bg"
    >
      <div className="space-y-6">{renderStepContent()}</div>
    </ModalCenter>
  );
});

AmazonFbmShipmentExportModal.displayName = 'AmazonFbmShipmentExportModal';
